// src/model/BoardState.ts
import { BishopMoveStrategy } from '../strategy/BishopMoveStrategy';
import { KnightMoveStrategy } from '../strategy/KnightMoveStrategy';
import { PawnMoveStrategy } from '../strategy/PawnMoveStrategy';
import { QueenMoveStrategy } from '../strategy/QueenMoveStrategy';
import { RookMoveStrategy } from '../strategy/RookMoveStrategy';
import { KingMoveStrategy } from '../strategy/KingMoveStrategy';
import { Piece } from './Piece';

export interface Position {
  row: number;
  col: number;
  piece?: Piece;
}

export class BoardState {
  pieces: Piece[];

  constructor(pieces: Piece[]) {
    this.pieces = pieces;
  }

  static initialBoardState(): BoardState {
    return new BoardState([
      // White pieces
      { type: 'rook', position: { row: 0, col: 0 }, color: 'white', moveStrategy: new RookMoveStrategy() },
      { type: 'knight', position: { row: 0, col: 1 }, color: 'white', moveStrategy: new KnightMoveStrategy() },
      { type: 'bishop', position: { row: 0, col: 2 }, color: 'white', moveStrategy: new BishopMoveStrategy() },
      { type: 'queen', position: { row: 0, col: 3 }, color: 'white', moveStrategy: new QueenMoveStrategy() },
      { type: 'king', position: { row: 0, col: 4 }, color: 'white', moveStrategy: new KingMoveStrategy() },
      { type: 'bishop', position: { row: 0, col: 5 }, color: 'white', moveStrategy: new BishopMoveStrategy() },
      { type: 'knight', position: { row: 0, col: 6 }, color: 'white', moveStrategy: new KnightMoveStrategy() },
      { type: 'rook', position: { row: 0, col: 7 }, color: 'white', moveStrategy: new RookMoveStrategy() },
      { type: 'pawn', position: { row: 1, col: 0 }, color: 'white', moveStrategy: new PawnMoveStrategy() },
      { type: 'pawn', position: { row: 1, col: 1 }, color: 'white', moveStrategy: new PawnMoveStrategy() },
      { type: 'pawn', position: { row: 1, col: 2 }, color: 'white', moveStrategy: new PawnMoveStrategy() },
      { type: 'pawn', position: { row: 1, col: 3 }, color: 'white', moveStrategy: new PawnMoveStrategy() },
      { type: 'pawn', position: { row: 1, col: 4 }, color: 'white', moveStrategy: new PawnMoveStrategy() },
      { type: 'pawn', position: { row: 1, col: 5 }, color: 'white', moveStrategy: new PawnMoveStrategy() },
      { type: 'pawn', position: { row: 1, col: 6 }, color: 'white', moveStrategy: new PawnMoveStrategy() },
      { type: 'pawn', position: { row: 1, col: 7 }, color: 'white', moveStrategy: new PawnMoveStrategy() },
      // Black pieces
      { type: 'rook', position: { row: 7, col: 0 }, color: 'black', moveStrategy: new RookMoveStrategy() },
      { type: 'knight', position: { row: 7, col: 1 }, color: 'black', moveStrategy: new KnightMoveStrategy() },
      { type: 'bishop', position: { row: 7, col: 2 }, color: 'black', moveStrategy: new BishopMoveStrategy() },
      { type: 'queen', position: { row: 7, col: 3 }, color: 'black', moveStrategy: new QueenMoveStrategy() },
      { type: 'king', position: { row: 7, col: 4 }, color: 'black', moveStrategy: new KingMoveStrategy() },
      { type: 'bishop', position: { row: 7, col: 5 }, color: 'black', moveStrategy: new BishopMoveStrategy() },
      { type: 'knight', position: { row: 7, col: 6 }, color: 'black', moveStrategy: new KnightMoveStrategy() },
      { type: 'rook', position: { row: 7, col: 7 }, color: 'black', moveStrategy: new RookMoveStrategy() },
      { type: 'pawn', position: { row: 6, col: 0 }, color: 'black', moveStrategy: new PawnMoveStrategy() },
      { type: 'pawn', position: { row: 6, col: 1 }, color: 'black', moveStrategy: new PawnMoveStrategy() },
      { type: 'pawn', position: { row: 6, col: 2 }, color: 'black', moveStrategy: new PawnMoveStrategy() },
      { type: 'pawn', position: { row: 6, col: 3 }, color: 'black', moveStrategy: new PawnMoveStrategy() },
      { type: 'pawn', position: { row: 6, col: 4 }, color: 'black', moveStrategy: new PawnMoveStrategy() },
      { type: 'pawn', position: { row: 6, col: 5 }, color: 'black', moveStrategy: new PawnMoveStrategy() },
      { type: 'pawn', position: { row: 6, col: 6 }, color: 'black', moveStrategy: new PawnMoveStrategy() },
      { type: 'pawn', position: { row: 6, col: 7 }, color: 'black', moveStrategy: new PawnMoveStrategy() },
    ]);
  }

  public getLegalMoves(piece: Piece): Position[] {
    const potentialMoves = piece.moveStrategy.getLegalMoves(piece.position, this);
    return potentialMoves.filter(move => !this.isInCheckAfterMove(piece.position, move));
  }

  private simulateMove(from: Position, to: Position): BoardState {
    const simulatedBoardState = new BoardState(
      this.pieces.map(piece => ({
        ...piece,
        position: piece.position.row === from.row && piece.position.col === from.col ? to : piece.position,
        moveStrategy: Object.create(Object.getPrototypeOf(piece.moveStrategy), Object.getOwnPropertyDescriptors(piece.moveStrategy))
      }))
    );

    // Remove any captured piece at the destination position
    simulatedBoardState.pieces = simulatedBoardState.pieces.filter(piece => !(piece.position.row === to.row && piece.position.col === to.col && piece.position !== to));

    return simulatedBoardState;
  }

  private isInCheckAfterMove(from: Position, to: Position): boolean {
    const simulatedBoardState = this.simulateMove(from, to);
    const currentPiece = this.pieces.find(p => p.position.row === from.row && p.position.col === from.col);
    const playerColor = currentPiece?.color === 'white' ? 'white' : 'black';
    const opponentColor = playerColor === 'white' ? 'black' : 'white';

    // Find the player's king position
    const king = simulatedBoardState.pieces.find(p => p.type === 'king' && p.color === playerColor);
    if (!king) return false;

    const kingPosition = king.position;

    // Iterate over each opponent piece to check if any can move to the king's position
    for (const piece of simulatedBoardState.pieces) {
      if (piece.color === opponentColor && piece.type !== 'king') {
        const legalMoves = piece.moveStrategy.getLegalMoves(piece.position, simulatedBoardState);
        if (legalMoves.some(move => move.row === kingPosition.row && move.col === kingPosition.col)) {
          return true; // The king is in check
        }
      }
    }

    return false; // The king is not in check
  }
}
